
import { useMemo } from 'react';
import { BehavioralScores, PersonalityProfile } from '@/types/resultsTypes';
import { sigmaToPercentile } from '@/utils/personalityCalculations';
import { getProfileType, profileTypeMapping } from '@/utils/profileTypeMapping';
import { useLanguage } from '@/contexts/LanguageContext';
import { useKeyTraits } from '@/hooks/useKeyTraits';
import { ProfileModelType } from '@/hooks/useProfileModel';
import { ProfileOverrideType } from '@/hooks/useProfileOverride';

// Map English profile type names to translation keys in personality.*
const profileTypeToTranslationKey: Record<string, string> = {
  'Luminary': 'inspirationalLeader',
  'Commander': 'commander',
  'Executive': 'executive',
  'Closer': 'dynamicNetworker',
  'Charger': 'visionaryLeader',
  'Uniter': 'socialInnovator',
  'Builder': 'strategicEntrepreneur',
  'Catalyst': 'specialist',
  'Achiever': 'achiever',
  'Pioneer': 'pioneer',
  'Independent': 'independent',
  'Connector': 'teamBuilder',
  'Supporter': 'supporter',
  'Spark': 'influencer',
  'Networker': 'networker',
  'Systematizer': 'systematizer',
  'Steady': 'facilitator',
  'Optimizer': 'collaborator',
  'Harmonizer': 'harmonizer',
  'Professional': 'professional',
};

export const usePersonalityProfile = (
  scores: BehavioralScores, 
  profileModel: ProfileModelType = 'standard',
  profileOverride: ProfileOverrideType = null,
  storedProfileName: string | null = null
): PersonalityProfile => {
  const { t } = useLanguage();
  const { getTraitForFactor, loading: traitsLoading } = useKeyTraits();

  return useMemo(() => {
    // If profile override is set, use that instead of calculating
    if (profileOverride) {
      console.log('Using profile override:', profileOverride);
      const overrideProfile = getProfileType(profileOverride);
      const overrideKey = profileTypeToTranslationKey[overrideProfile.type] || 'professional';
      
      return {
        type: t(`personality.${overrideKey}.type`) || overrideProfile.type,
        description: t(`personality.${overrideKey}.description`) || overrideProfile.description,
        traits: overrideProfile.traits,
        workStyle: overrideProfile.workStyle,
        communication: overrideProfile.communication
      };
    }

    // If a stored profile name is provided (from DB), use it as source of truth
    // This ensures the results page matches the admin dashboard exactly
    if (storedProfileName) {
      // Strip "The " prefix if present to get the type name
      const typeName = storedProfileName.startsWith('The ') 
        ? storedProfileName.substring(4) 
        : storedProfileName;
      const translationKey = profileTypeToTranslationKey[typeName] || 'professional';
      
      // Still need percentiles for dynamic traits
      const dominancePercentile = sigmaToPercentile(scores.dominance);
      const extraversionPercentile = sigmaToPercentile(scores.extraversion);
      const patiencePercentile = sigmaToPercentile(scores.patience);
      const formalityPercentile = sigmaToPercentile(scores.formality);

      const getTraits = (): string[] => {
        if (traitsLoading) return ['Loading...'];
        return [
          getTraitForFactor('dominance', dominancePercentile),
          getTraitForFactor('extraversion', extraversionPercentile),
          getTraitForFactor('patience', patiencePercentile),
          getTraitForFactor('formality', formalityPercentile)
        ];
      };

      // Find matching profile type data for workStyle/communication
      const profileTypeData = Object.values(profileTypeMapping).find((p: any) => p.type === typeName) as any;

      console.log('usePersonalityProfile: Using stored profile name:', { storedProfileName, typeName, translationKey });

      return {
        type: t(`personality.${translationKey}.type`) || typeName,
        description: t(`personality.${translationKey}.description`) || profileTypeData?.description || '',
        traits: getTraits(),
        workStyle: profileTypeData?.workStyle || 'adaptableProfessional',
        communication: profileTypeData?.communication || 'situational'
      };
    }

    // Use different thresholds based on profile model
    const percentileThreshold = profileModel === 'standard' ? 50 : 55; // Alternative model uses slightly higher threshold
    
    const dominancePercentile = sigmaToPercentile(scores.dominance);
    const extraversionPercentile = sigmaToPercentile(scores.extraversion);
    const patiencePercentile = sigmaToPercentile(scores.patience);
    const formalityPercentile = sigmaToPercentile(scores.formality);
    
    console.log('Personality Profile Debug - Raw Scores & Percentiles:', {
      profileModel,
      rawScores: {
        dominance: scores.dominance,
        extraversion: scores.extraversion,
        patience: scores.patience,
        formality: scores.formality
      },
      percentiles: {
        dominancePercentile,
        extraversionPercentile,
        patiencePercentile,
        formalityPercentile
      },
      threshold: percentileThreshold
    });
    
    const d = dominancePercentile >= percentileThreshold ? 'H' : 'L';
    const i = extraversionPercentile >= percentileThreshold ? 'H' : 'L';
    const s = patiencePercentile >= percentileThreshold ? 'H' : 'L';
    const c = formalityPercentile >= percentileThreshold ? 'H' : 'L';
    
    let profileKey = `${d}${i}${s}${c}`;
    
    console.log('Personality Profile Debug - Classification:', {
      classifications: { d, i, s, c },
      profileKey,
      model: profileModel
    });
    
    if (profileKey === 'HHLL') {
      const highestIsDominance = scores.dominance >= scores.extraversion;
      const structureOverPatience = scores.formality > scores.patience;
      
      if (highestIsDominance) {
        profileKey = structureOverPatience ? 'HHLL-AHDH' : 'HHLL-AHDL';
      } else {
        profileKey = structureOverPatience ? 'HHLL-BHDH' : 'HHLL-BHDL';
      }
      
      console.log('HHLL Sub-classification in hook:', {
        highestIsDominance,
        structureOverPatience,
        finalProfileKey: profileKey
      });
    }

    const profileTypeData = getProfileType(profileKey);
    
    console.log('Personality Profile Debug - Final:', {
      profileKey,
      profileTypeData,
      model: profileModel
    });
    
    // Generate dynamic traits based on A-B-C-D factors using the key traits table
    const getTraits = (): string[] => {
      if (traitsLoading) {
        return ['Loading...'];
      }

      return [
        getTraitForFactor('dominance', dominancePercentile),
        getTraitForFactor('extraversion', extraversionPercentile),
        getTraitForFactor('patience', patiencePercentile),
        getTraitForFactor('formality', formalityPercentile)
      ];
    };

    const getWorkStyleKey = (profileTypeData: any): string => {
      if (!profileTypeData) return 'adaptableProfessional';
      return profileTypeData.workStyle || 'adaptableProfessional';
    };

    const getCommunicationKey = (profileTypeData: any): string => {
      if (!profileTypeData) return 'situational';
      return profileTypeData.communication || 'situational';
    };

    // Translate profile type and description
    const englishType = profileTypeData?.type || 'Professional';
    const translationKey = profileTypeToTranslationKey[englishType] || 'professional';
    const translatedType = t(`personality.${translationKey}.type`) || englishType;
    const translatedDescription = t(`personality.${translationKey}.description`) || profileTypeData?.description || '';

    return {
      type: translatedType,
      description: translatedDescription,
      traits: getTraits(),
      workStyle: getWorkStyleKey(profileTypeData),
      communication: getCommunicationKey(profileTypeData)
    };
  }, [scores, t, getTraitForFactor, traitsLoading, profileModel, profileOverride]);
};

